import { differenceInHours, differenceInMinutes } from 'date-fns';
import { t } from 'i18next';
import {
  EventTimeline,
  EventType,
  KnownMembership,
  MatrixClient,
  MatrixEvent,
  MsgType,
  NotificationCountType,
  Room,
  RoomType,
} from 'matrix-js-sdk/src/matrix';

import {
  formatTimestampToDateConversations,
  getFormattedGuestDisplayName,
} from 'modules/MeetingChat/Messages/Message/utils';
import {
  buildSyntheticStartItem,
  findMissingStartPairs,
  resolveSyntheticStartFromEnd,
} from 'modules/Messenger/components/Messenger/meetingTimelineBackfill';
import { classifyMemberEvent, classifyPowerLevelsEvent } from 'modules/Messenger/components/Messenger/timelineUtils';
import { getChatDisplayName } from 'modules/Messenger/utils/chatDisplayName';
import { getMyOriginalJoinTimestamp } from 'modules/Messenger/utils/myOriginalJoinTimestamp';
import { stripReplyFallback } from 'modules/Messenger/utils/stripReplyFallback';
import { getRoomSortRecencyTimestamp, sortRooms } from 'modules/RoomsListItem/utils';
import { getWorkspaceRoomIds } from 'modules/Workspaces/WorkspacesProvider/utils';
import { CustomMeedioMatrixEvents, getJoinedMemberCount, isSystemUser } from 'utils/matrixUtils';
import { getCanonicalParentRoom } from 'utils/roomHierarchyUtils';

import { ConversationsFilterEnum } from './ConversationsFilter/ConversationsChip';

const getLastMessageText = (
  eventType: string,
  msgtype: MsgType | undefined,
  body: unknown,
  content: Record<string, unknown> | undefined
): string => {
  if (eventType === CustomMeedioMatrixEvents.SCHEDULED_MEETING) {
    const status = content?.status;
    switch (status) {
      case 'updated':
        return t('messenger.preview_scheduled_meeting_updated');
      case 'cancelled':
        return t('messenger.preview_scheduled_meeting_cancelled');
      case 'created':
      default:
        return t('messenger.preview_scheduled_meeting_created');
    }
  }
  if (eventType === CustomMeedioMatrixEvents.MEETING_STARTED) return t('messenger.preview_meeting_started');
  if (eventType === CustomMeedioMatrixEvents.MEETING_ENDED) return t('messenger.preview_meeting_ended');

  if (msgtype === MsgType.Text && typeof body === 'string') return stripReplyFallback(body);

  switch (msgtype) {
    case MsgType.Image:
      return t('messenger.sent_an_image');
    case MsgType.Video:
      return t('messenger.sent_a_video');
    case MsgType.Audio:
      return t('messenger.sent_an_audio');
    case MsgType.File:
      return t('messenger.sent_a_file');
    default:
      return t('messenger.sent_a_message');
  }
};

export type LastMessage = { text: string; timestamp: number; senderDisplayName: string };

// Member / power-level action → conversation-list preview phrase. The subject user's
// name is prefixed by the caller (getLastMessagePreview), so these are phrase-only.
const MEMBER_ACTION_PREVIEW_KEY: Partial<Record<string, string>> = {
  joined: 'messenger.preview_user_joined',
  left: 'messenger.preview_user_left',
  added: 'messenger.preview_user_added',
  kicked: 'messenger.preview_user_kicked',
  'workspace-kicked': 'messenger.preview_user_kicked_from_workspace',
};

// Meeting join/leave lifecycle event type → preview phrase.
const MEETING_USER_ACTION_PREVIEW_KEY: Record<string, string> = {
  [CustomMeedioMatrixEvents.USER_JOINED_MEETING]: 'messenger.preview_joined_meeting',
  [CustomMeedioMatrixEvents.USERS_JOINED_MEETING]: 'messenger.preview_joined_meeting',
  [CustomMeedioMatrixEvents.USER_LEFT_MEETING]: 'messenger.preview_left_meeting',
};

// Meeting lifecycle events that pre-date the current user's join are historical noise
// (BUG-065) — they belong to sessions the user was not part of, so they must not surface
// as the room's last-message preview.
const MEETING_LIFECYCLE_PREVIEW_TYPES = new Set<string>([
  CustomMeedioMatrixEvents.MEETING_STARTED,
  CustomMeedioMatrixEvents.MEETING_ENDED,
  CustomMeedioMatrixEvents.USER_JOINED_MEETING,
  CustomMeedioMatrixEvents.USERS_JOINED_MEETING,
  CustomMeedioMatrixEvents.USER_LEFT_MEETING,
]);

const getMyJoinTimestamp = (room: Room, myUserId: string | null, events?: MatrixEvent[]): number =>
  getMyOriginalJoinTimestamp(room, myUserId, events);

const resolveDisplayName = (room: Room, userId: string, myUserId: string | null): string =>
  userId && userId === myUserId ? t('you') : getFormattedGuestDisplayName(room.getMember(userId)?.rawDisplayName);

type ResolvedPreview = { text: string; senderDisplayName: string };

/** FR-4 synthetic start — mirrors open-chat `SyntheticMeetingStartedItem` for list preview. */
const resolveSyntheticMeetingStartPreview = (events: MatrixEvent[], room: Room): LastMessage | null => {
  let best: LastMessage | null = null;

  for (const { endEvent, meetingRoomId } of findMissingStartPairs(events)) {
    const resolved =
      resolveSyntheticStartFromEnd(endEvent) ??
      (room.name ? { meetingName: room.name, isInstant: false } : { meetingName: '', isInstant: true });

    const synthetic = buildSyntheticStartItem({ endEvent, meetingRoomId, resolved });
    const text = resolved.isInstant
      ? t('messenger.preview_meeting_started')
      : t('messenger.preview_scheduled_meeting_started');
    const senderDisplayName = resolved.isInstant
      ? resolved.startedByName ?? t('participant')
      : resolved.meetingName || t('participant');

    const candidate: LastMessage = {
      text,
      timestamp: synthetic.timestamp,
      senderDisplayName,
    };
    if (!best || candidate.timestamp > best.timestamp) best = candidate;
  }

  return best;
};

/**
 * Resolve a single timeline event into a conversation-list preview, or null if the event
 * is not previewable (reactions, state changes, etc.). Mirrors the open-chat timeline's
 * classification so event-only rooms (no text messages) show the actual last event
 * instead of falling through to the generic "Encrypted message" placeholder (#8198).
 */
const resolveEventPreview = (
  event: MatrixEvent,
  room: Room,
  myUserId: string | null,
  myJoinTs: number
): ResolvedPreview | null => {
  const type = event.getType();
  const content = event.getContent() as Record<string, unknown> | undefined;

  if (type === EventType.RoomMember) {
    const result = classifyMemberEvent(event, room);
    const key = result ? MEMBER_ACTION_PREVIEW_KEY[result.actionType] : undefined;
    if (!result || !key) return null;
    return { text: t(key), senderDisplayName: resolveDisplayName(room, result.userId, myUserId) };
  }

  if (type === EventType.RoomPowerLevels) {
    const promotions = classifyPowerLevelsEvent(event);
    if (!promotions.length) return null;
    const promoted = promotions[promotions.length - 1];
    return {
      text: t('messenger.preview_user_became_admin'),
      senderDisplayName: resolveDisplayName(room, promoted.userId, myUserId),
    };
  }

  // Meeting lifecycle events from before the user joined are skipped (BUG-065).
  if (MEETING_LIFECYCLE_PREVIEW_TYPES.has(type) && event.getTs() < myJoinTs) return null;

  const meetingUserKey = MEETING_USER_ACTION_PREVIEW_KEY[type];
  if (meetingUserKey) {
    const contentUserId = typeof content?.user_id === 'string' ? (content.user_id as string) : '';
    const subjectUserId = contentUserId || event.getSender() || '';
    if (!subjectUserId || isSystemUser(subjectUserId)) return null;
    return { text: t(meetingUserKey), senderDisplayName: resolveDisplayName(room, subjectUserId, myUserId) };
  }

  const isMessage = type === EventType.RoomMessage || type === EventType.RoomMessageEncrypted;
  const isMeetingStatus =
    type === CustomMeedioMatrixEvents.SCHEDULED_MEETING ||
    type === CustomMeedioMatrixEvents.MEETING_STARTED ||
    type === CustomMeedioMatrixEvents.MEETING_ENDED;
  if (isMessage || isMeetingStatus) {
    const msgtype = content?.msgtype as MsgType | undefined;
    const body = content?.body;
    const text = getLastMessageText(type, msgtype, body, content);
    return { text, senderDisplayName: resolveDisplayName(room, event.getSender() || '', myUserId) };
  }

  return null;
};

/**
 * Walk a chronological (oldest-first) event array backward and resolve the first
 * previewable event into a LastMessage. Shared by the live-timeline path
 * (`getLastMessage`) and the conversation-list preview backfill (#8198), which
 * supplies a filtered `/messages` page when the live timeline only holds
 * unpreviewable noise (e.g. member profile updates).
 *
 * Also considers FR-4 synthetic meeting-start rows (MEETING_ENDED without a prior
 * MEETING_STARTED) so the list matches the open chat's last visible lifecycle row.
 */
export const resolveLastMessageFromEvents = (
  events: MatrixEvent[],
  room: Room,
  matrixClient: MatrixClient | null
): LastMessage | null => {
  const myUserId = matrixClient?.getUserId() ?? null;
  const myJoinTs = getMyJoinTimestamp(room, myUserId, events);
  const endsWithSynthetic = new Set(
    findMissingStartPairs(events)
      .map(({ endEvent }) => endEvent.getId?.())
      .filter((id): id is string => !!id)
  );

  let best: LastMessage | null = null;

  for (let i = events.length - 1; i >= 0; i--) {
    const event = events[i];
    if (event.isDecryptionFailure()) continue;
    const content = event.getContent();
    if (content?.['m.relates_to']?.rel_type === 'm.replace') continue;
    if (endsWithSynthetic.has(event.getId?.() ?? '')) continue;

    const preview = resolveEventPreview(event, room, myUserId, myJoinTs);
    if (!preview) continue;

    best = { text: preview.text, timestamp: event.getTs(), senderDisplayName: preview.senderDisplayName };
    break;
  }

  const synthetic = resolveSyntheticMeetingStartPreview(events, room);
  if (synthetic && (!best || synthetic.timestamp > best.timestamp)) return synthetic;

  return best;
};

export const getLastMessage = (room: Room, matrixClient: MatrixClient | null): LastMessage | null => {
  const timeline = room.getLiveTimeline().getEvents();
  return resolveLastMessageFromEvents(timeline, room, matrixClient);
};

export const formatLastMessageTime = (timestamp: number): string => {
  const now = new Date();
  const date = new Date(timestamp);
  const minutesAgo = differenceInMinutes(now, date);
  const hoursAgo = differenceInHours(now, date);

  if (minutesAgo < 1) return t('messenger.time_just_now');
  if (hoursAgo < 1) return t('messenger.time_minutes_ago', { count: minutesAgo });
  if (hoursAgo < 24) return t('messenger.time_hours_ago', { count: hoursAgo });
  return formatTimestampToDateConversations(timestamp);
};

export const filterRoomsBySearch = (
  rooms: Room[],
  searchTerm: string,
  matrixClient: MatrixClient | null = null
): Room[] => {
  if (!searchTerm.trim()) return rooms;

  const normalized = searchTerm.toLowerCase().trim();

  return rooms.filter((room) => {
    const display = getChatDisplayName(room, matrixClient, t);
    return display.full.toLowerCase().includes(normalized);
  });
};

export const filterRoomsByFilter = (rooms: Room[], filter: ConversationsFilterEnum) => {
  if (filter === ConversationsFilterEnum.All) return rooms;

  return rooms.filter((room) => {
    if (filter === ConversationsFilterEnum.Unread) {
      return (room.getRoomUnreadNotificationCount(NotificationCountType.Total) ?? 0) > 0;
    }
    if (filter === ConversationsFilterEnum.Groups) {
      return getJoinedMemberCount(room) > 2;
    }
    return true;
  });
};

export const isArchivedRoom = (room: Room): boolean => !!room.tags?.['m.lowpriority'];

export const isMessengerRoom = (room: Room): boolean => {
  if (room.getMyMembership() !== KnownMembership.Join || room.getType() === RoomType.Space) return false;
  const messengerEvent = room
    .getLiveTimeline()
    .getState(EventTimeline.FORWARDS)
    ?.getStateEvents(CustomMeedioMatrixEvents.MESSENGER_ROOM)?.[0];

  return !!messengerEvent;
};

export const getMessengerRooms = (matrixClient: MatrixClient, workspaceId: string | null = null): Room[] => {
  if (!matrixClient) return [];

  const messengerRooms = matrixClient.getRooms().filter(isMessengerRoom);
  const userId = matrixClient.getUserId();

  if (workspaceId) {
    const workspaceRoomIds = getWorkspaceRoomIds(matrixClient, workspaceId);
    if (!workspaceRoomIds) return [];

    return sortRooms(
      messengerRooms.filter(({ roomId }) => workspaceRoomIds.has(roomId)),
      userId
    );
  }

  // Personal workspace: exclude rooms that have a parent (i.e. belong to a workspace/space)
  return sortRooms(
    messengerRooms.filter((room) => !getCanonicalParentRoom(room)),
    userId
  );
};

export const getAllMessengerRooms = (matrixClient: MatrixClient): Room[] => {
  if (!matrixClient) return [];
  return matrixClient.getRooms().filter(isMessengerRoom);
};

export const getRoomTimestamp = (room: Room, userId?: string | null): number =>
  getRoomSortRecencyTimestamp(room, userId);

/**
 * Move a room to its correct position in a recency-sorted array.
 * Avoids full re-sort by only reinserting the affected room.
 */
export const moveRoomToRecencyPosition = (
  rooms: Room[],
  roomId: string,
  getTimestamp: (room: Room) => number = getRoomTimestamp
): Room[] => {
  const idx = rooms.findIndex((r) => r.roomId === roomId);
  if (idx === -1) return rooms;

  const room = rooms[idx];
  const roomTs = getTimestamp(room);
  const rest = [...rooms.slice(0, idx), ...rooms.slice(idx + 1)];

  // Find first room that is older (lower timestamp) than this room
  const insertIdx = rest.findIndex((r) => getTimestamp(r) < roomTs);
  const newIdx = insertIdx === -1 ? rest.length : insertIdx;

  return [...rest.slice(0, newIdx), room, ...rest.slice(newIdx)];
};
